// MerchantTermsScreen.js
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MerchantTermsScreen.css';

import backIcon from '../assets/backspace-icon.png';

export default function MerchantTermsScreen() {
  const navigate = useNavigate();

  const scrollRef = useRef(null);
  const [showTopShadow, setShowTopShadow] = useState(false);

  const terms = useMemo(() => ([
    {
      title: '1) Merchant Eligibility',
      bullets: [
        'Have an active in-app Pi wallet. This confirms you are verified (KYC) through the Pi Network process and eligible to participate in marketplace selling features.',
        'Provide accurate merchant/store information (business name, details, and any required identifiers).',
        'Be able to operate as a serious merchant who understands how to run a business online (orders, communication, delivery, and customer care).',
      ],
    },
    {
      title: '2) Your Responsibilities as a Merchant',
      bullets: [
        'List products/services honestly and clearly, with accurate descriptions and images.',
        'Set realistic and fair pricing. You are free to set your own prices, but pricing that appears misleading, exploitative, or unrealistic may be flagged.',
        'Keep product listings up to date, including availability/quantity, condition (e.g., new/used), and expected delivery timeframe (where applicable).',
        'Communicate professionally with customers and handle issues in good faith.',
      ],
    },
    {
      title: '3) Shipping & Delivery (Merchant-Handled)',
      bullets: [
        'BarterFi does not provide shipping, delivery, or courier services.',
        'You are responsible for packaging, shipping, delivery, and customer delivery communication.',
        'Any delivery fees or logistics costs should be included in your pricing or clearly factored into your listing.',
        'When listing a product, you may select available delivery methods (including “Other” where applicable) and must ensure the method chosen is realistic and deliverable.',
      ],
    },
    {
      title: '4) Escrow Protection for Orders',
      bullets: [
        'All marketplace orders are processed through escrow to protect both buyer and seller.',
        'Escrow helps ensure the buyer is protected if goods/services are not delivered as promised, and the merchant is protected through structured confirmation before completion.',
        'You agree to follow the escrow process and cooperate with reasonable dispute resolution steps.',
        'Products with active or pending escrow orders may be restricted from being removed or altered in ways that break the order process.',
      ],
    },
    {
      title: '5) Fees & Commission',
      bullets: [
        'BarterFi charges a 10% commission on each completed escrow order.',
        'Cancelled or refunded orders are not charged a commission.',
      ],
    },
    {
      title: '6) Prohibited Merchant Behavior',
      bullets: [
        'Selling fake, fraudulent, or misleading items.',
        'Misrepresenting product condition, brand, origin, or functionality.',
        'Scamming buyers or attempting to bypass platform protections.',
        'Abusing customers (harassment, threats, manipulation).',
        'Repeated failure to deliver orders as promised.',
        'Any behavior that puts users, the ecosystem, or the platform at risk.',
      ],
    },
    {
      title: '7) Suspension, Revocation, and Termination',
      bullets: [
        'BarterFi may suspend or revoke merchant access to protect buyers and platform integrity.',
        'Automatic revocation for non-serious merchants: if you are approved but do not activate your merchant subscription within a reasonable onboarding period, your merchant offer may be revoked.',
        'Suspension for violations: if you are reported or detected selling fake or fraudulent items, your store may be suspended. If unresolved, merchant status may be terminated.',
        'Severe cases may lead to full account bans to protect users.',
        'Product moderation: BarterFi reserves the right to hide or remove products that violate rules, mislead users, or harm trust in the marketplace.',
      ],
    },
    {
      title: '8) Platform Rights & Updates',
      bullets: [
        'BarterFi may update these terms over time.',
        'Continued merchant use of the platform means you agree to follow the latest version of the merchant terms.',
      ],
    },
    {
      title: '9) Acceptance',
      bullets: [
        'By registering as a merchant, you confirm that you have read and agree to these Merchant Terms & Responsibilities and understand that violations may lead to suspension or revocation.',
      ],
    },
  ]), []);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowTopShadow(el.scrollTop > 6);
  };

  return (
    <div className="merchant-terms-screen">
      <div className={`merchant-terms-topbar ${showTopShadow ? 'shadow' : ''}`}>
        <button
          className="mts-back"
          type="button"
          onClick={() => navigate(-1)}
        >
          <img src={backIcon} alt="Back" />
        </button>

        <div className="mts-title">Merchant Terms</div>
        <div className="mts-right" />
      </div>

      <div
        className="merchant-terms-body"
        ref={scrollRef}
        onScroll={onScroll}
      >
        <p className="mts-intro">
          Please read these terms carefully before registering your store.
          These rules help protect buyers, merchants, and the BarterFi marketplace.
        </p>

        {terms.map((sec) => (
          <div key={sec.title} className="mts-section">
            <div className="mts-h">{sec.title}</div>
            <ul className="mts-list">
              {sec.bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        ))}

        <div className="mts-bottom-spacer" />
      </div>
    </div>
  );
}
